import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [amount, setAmount] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let count = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setAmount(data.amount);
          setState("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        if (count++ >= MAX_POLLS) {
          setState("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        if (count++ >= MAX_POLLS) setState("error");
        else timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-5 pt-24" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-lg rounded-sm border border-[#E4DDD0] bg-cream-2 p-12 text-center"
      >
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-gold" />
            <h1 className="mt-6 font-serif text-3xl text-moss">Confirming your order…</h1>
            <p className="mt-2 text-muted">One moment while we verify your payment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.24em] text-gold">Order Confirmed</p>
            <h1 className="mt-3 font-serif text-4xl text-moss">Your ritual is on its way.</h1>
            {amount != null && <p className="mt-3 text-muted">Total charged: <span className="text-moss font-medium">${Number(amount).toFixed(2)}</span></p>}
            <p className="mt-2 text-sm text-muted">Thank you for feeding your skin what it can actually read.</p>
            <p className="mt-4 font-mono text-[11px] text-muted">Ref: {sessionId?.slice(-12)}</p>
            <Link to="/shop" className="btn-gold mt-8 inline-flex items-center gap-2 rounded-sm px-7 py-3.5 text-sm font-semibold uppercase tracking-wider" data-testid="success-continue">
              Continue shopping <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-gold" />
            <h1 className="mt-6 font-serif text-3xl text-moss">Payment processing</h1>
            <p className="mt-2 text-muted">Your payment is still being confirmed. You'll receive an email shortly.</p>
            <Link to="/" className="btn-gold mt-8 inline-flex rounded-sm px-7 py-3.5 text-sm font-semibold uppercase tracking-wider">Back home</Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-terracotta" />
            <h1 className="mt-6 font-serif text-3xl text-moss">Something went wrong</h1>
            <p className="mt-2 text-muted">We couldn't confirm this payment. Please try again or contact us.</p>
            <Link to="/cart" className="btn-gold mt-8 inline-flex rounded-sm px-7 py-3.5 text-sm font-semibold uppercase tracking-wider">Return to cart</Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
